import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Cookie, Settings, Eye, BarChart3, Shield, Users, MessageCircle, ToggleLeft, ToggleRight } from 'lucide-react';
import SEOHead from './SEOHead';

const CookiePolicy = () => {
  const lastUpdated = "January 1, 2025";
  
  // Cookie preferences state (for demonstration)
  const [cookiePreferences, setCookiePreferences] = useState({
    essential: true, // Always on
    analytics: true,
    marketing: false,
    personalization: true
  });

  const toggleCookiePreference = (type) => {
    if (type !== 'essential') { // Essential cookies cannot be disabled
      setCookiePreferences(prev => ({
        ...prev,
        [type]: !prev[type]
      }));
    }
  };

  const cookieTypes = [
    {
      id: "essential",
      title: "Essential Cookies",
      icon: <Shield className="w-5 h-5" />,
      description: "Required for basic platform functionality and cannot be disabled",
      always_on: true,
      examples: [
        "User authentication and session management",
        "Security tokens and CSRF protection",
        "Video call connection and quality optimization",
        "Basic platform navigation and functionality"
      ],
      duration: "Session or up to 1 year"
    },
    {
      id: "analytics",
      title: "Analytics & Performance",
      icon: <BarChart3 className="w-5 h-5" />,
      description: "Help us understand how users interact with our platform to improve services",
      examples: [
        "Page views and user journey tracking",
        "Speed dating session success rates",
        "Feature usage and engagement metrics",
        "Technical performance monitoring"
      ],
      duration: "Up to 2 years"
    },
    {
      id: "personalization",
      title: "Personalization",
      icon: <Users className="w-5 h-5" />,
      description: "Enable AI-powered matching and personalized user experiences",
      examples: [
        "AI matching preferences and learning data",
        "Personalized icebreaker suggestions",
        "User interface customization settings",
        "Conversation coaching improvements"
      ],
      duration: "Up to 1 year"
    },
    {
      id: "marketing",
      title: "Marketing & Advertising",
      icon: <Eye className="w-5 h-5" />,
      description: "Show relevant content and measure advertising effectiveness",
      examples: [
        "Targeted content and feature recommendations",
        "Email campaign effectiveness tracking",
        "Social media integration and sharing",
        "Third-party advertising measurement"
      ],
      duration: "Up to 13 months"
    }
  ];

  const thirdPartyServices = [
    {
      name: "Google Analytics",
      purpose: "Website traffic analysis and user behavior insights",
      cookies: "GA tracking cookies (_ga, _gat, _gid)",
      optOut: "https://tools.google.com/dlpage/gaoptout"
    },
    {
      name: "Stripe",
      purpose: "Payment processing and subscription management",
      cookies: "Payment session and fraud prevention cookies",
      optOut: "Required for payment processing"
    },
    {
      name: "SendGrid",
      purpose: "Email delivery and engagement tracking",
      cookies: "Email tracking pixels and delivery confirmation",
      optOut: "Unsubscribe from emails to opt-out"
    },
    {
      name: "OpenAI/AI Services", 
      purpose: "AI matching and conversation assistance",
      cookies: "Session tokens for AI service optimization",
      optOut: "Disable AI features in account settings"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-pink-50 overflow-x-hidden">
      <SEOHead 
        title="Cookie Policy | Shot Clock Dating"
        description="Learn about cookies and tracking technologies used by Shot Clock Dating. Manage your cookie preferences for AI-powered speed dating."
        keywords="cookie policy, privacy settings, tracking cookies, AI cookies, personalization cookies, analytics cookies"
        url="https://shotclockdating.com/cookie-policy"
      />

      {/* Header */}
      <div className="bg-gradient-to-r from-rose-600 to-pink-600 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <motion.div
            className="flex items-center justify-center mb-6"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Cookie className="w-12 h-12 mr-4" />
            <h1 className="text-4xl md:text-5xl font-bold">Cookie Policy</h1>
          </motion.div>
          <motion.p 
            className="text-xl text-rose-100 mb-4"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.8 }}
          >
            How we use cookies to enhance your speed dating experience
          </motion.p>
          <motion.p 
            className="text-rose-200"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.8 }}
          >
            Last Updated: {lastUpdated}
          </motion.p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-16">
        {/* Introduction */}
        <motion.div 
          className="bg-white rounded-xl shadow-lg border border-gray-100 p-8 mb-8"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.8 }}
        >
          <div className="flex items-center mb-6">
            <Settings className="w-6 h-6 text-rose-600 mr-3" />
            <h2 className="text-2xl font-bold text-gray-800">What Are Cookies?</h2>
          </div>
          <p className="text-gray-600 leading-relaxed mb-4">
            Cookies are small text files stored on your device when you visit websites. At Shot Clock Dating, we use cookies and similar technologies to provide, protect, and improve our AI-powered speed dating platform.
          </p>
          <p className="text-gray-600 leading-relaxed mb-4">
            These technologies help us remember your preferences, understand how you use our platform, personalize your matching experience, and ensure our services work properly across different devices and sessions.
          </p>
          <div className="bg-rose-50 border border-rose-200 rounded-lg p-4">
            <p className="text-rose-700 text-sm">
              <strong>Your Control:</strong> You can manage your cookie preferences at any time using the controls below or through your browser settings.
            </p>
          </div>
        </motion.div>

        {/* Cookie Preferences Manager */}
        <motion.div 
          className="bg-white rounded-xl shadow-lg border border-gray-100 p-8 mb-8"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.8 }}
        >
          <div className="flex items-center mb-6">
            <Settings className="w-6 h-6 text-rose-600 mr-3" />
            <h2 className="text-2xl font-bold text-gray-800">Manage Your Cookie Preferences</h2>
          </div>
          
          <div className="space-y-6">
            {cookieTypes.map((cookieType, index) => (
              <div key={cookieType.id} className="border border-gray-200 rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center">
                    <div className="text-rose-600 mr-3">{cookieType.icon}</div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-800">{cookieType.title}</h3>
                      <p className="text-sm text-gray-600">{cookieType.description}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center">
                    {cookieType.always_on ? (
                      <span className="text-sm text-gray-500 mr-3">Always On</span>
                    ) : (
                      <button
                        onClick={() => toggleCookiePreference(cookieType.id)}
                        className="mr-3"
                      >
                        {cookiePreferences[cookieType.id] ? (
                          <ToggleRight className="w-8 h-8 text-green-600" />
                        ) : (
                          <ToggleLeft className="w-8 h-8 text-gray-400" />
                        )}
                      </button>
                    )}
                  </div>
                </div>
                
                <div className="mb-4">
                  <h4 className="font-medium text-gray-700 mb-2">Examples:</h4>
                  <ul className="space-y-1">
                    {cookieType.examples.map((example, i) => (
                      <li key={i} className="flex items-start text-sm text-gray-600">
                        <div className="w-1.5 h-1.5 bg-rose-400 rounded-full mt-2 mr-2 flex-shrink-0"></div>
                        {example}
                      </li>
                    ))}
                  </ul>
                </div>
                
                <p className="text-xs text-gray-500">
                  <strong>Duration:</strong> {cookieType.duration}
                </p>
              </div>
            ))}
          </div>
          
          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600 mb-3">
              <strong>Note:</strong> These preferences are saved locally and may need to be reset if you clear your browser data.
            </p>
            <button className="bg-rose-600 text-white px-6 py-2 rounded-lg hover:bg-rose-700 transition-colors">
              Save Preferences
            </button>
          </div>
        </motion.div>

        {/* Third-Party Services */}
        <motion.div 
          className="bg-white rounded-xl shadow-lg border border-gray-100 p-8 mb-8"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.0, duration: 0.8 }}
        >
          <div className="flex items-center mb-6">
            <Users className="w-6 h-6 text-rose-600 mr-3" />
            <h2 className="text-2xl font-bold text-gray-800">Third-Party Services</h2>
          </div>
          <p className="text-gray-600 mb-6">
            We work with trusted third-party services to provide the best speed dating experience. These services may set their own cookies:
          </p>
          
          <div className="space-y-4">
            {thirdPartyServices.map((service, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-4">
                <div className="flex justify-between items-start mb-2">
                  <h3 className="font-semibold text-gray-800">{service.name}</h3>
                  <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">Third-party</span>
                </div>
                <p className="text-sm text-gray-600 mb-2">{service.purpose}</p>
                <div className="text-xs text-gray-500">
                  <p><strong>Cookies:</strong> {service.cookies}</p>
                  <p><strong>Opt-out:</strong> {service.optOut}</p>
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Browser Controls */}
        <motion.div 
          className="bg-white rounded-xl shadow-lg border border-gray-100 p-8 mb-8"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2, duration: 0.8 }}
        >
          <div className="flex items-center mb-6">
            <Shield className="w-6 h-6 text-rose-600 mr-3" />
            <h2 className="text-2xl font-bold text-gray-800">Browser Cookie Controls</h2>
          </div>
          
          <p className="text-gray-600 mb-6">
            You can also manage cookies directly through your browser settings. Here's how to access cookie controls in popular browsers:
          </p>
          
          <div className="grid md:grid-cols-2 gap-4">
            <div className="border border-gray-200 rounded-lg p-4">
              <h3 className="font-semibold text-gray-800 mb-2">Chrome</h3>
              <p className="text-sm text-gray-600">Settings → Privacy and security → Cookies and other site data</p>
            </div>
            <div className="border border-gray-200 rounded-lg p-4">
              <h3 className="font-semibold text-gray-800 mb-2">Firefox</h3>
              <p className="text-sm text-gray-600">Preferences → Privacy & Security → Cookies and Site Data</p>
            </div>
            <div className="border border-gray-200 rounded-lg p-4">
              <h3 className="font-semibold text-gray-800 mb-2">Safari</h3>
              <p className="text-sm text-gray-600">Preferences → Privacy → Manage Website Data</p>
            </div>
            <div className="border border-gray-200 rounded-lg p-4">
              <h3 className="font-semibold text-gray-800 mb-2">Edge</h3>
              <p className="text-sm text-gray-600">Settings → Cookies and site permissions → Cookies and site data</p>
            </div>
          </div>
          
          <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-yellow-700 text-sm">
              <strong>Important:</strong> Disabling essential cookies may prevent Shot Clock Dating from working properly, affecting your ability to participate in speed dating sessions.
            </p>
          </div>
        </motion.div>

        {/* Contact Information */}
        <motion.div 
          className="bg-gradient-to-r from-rose-100 to-pink-100 rounded-xl p-8"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.4, duration: 0.8 }}
        >
          <div className="flex items-center mb-6">
            <MessageCircle className="w-6 h-6 text-rose-600 mr-3" />
            <h2 className="text-2xl font-bold text-gray-800">Questions About Cookies?</h2>
          </div>
          <p className="text-gray-700 mb-6">
            If you have questions about our cookie practices, need help managing your preferences, or want to understand how cookies affect your speed dating experience, please contact us:
          </p>
          <div className="bg-white rounded-lg p-4 border border-rose-200">
            <p className="text-gray-700">
              <strong>Email:</strong>{' '}
              <a 
                href="mailto:admin@shotclockdating.com"
                className="text-rose-600 hover:text-rose-700 underline font-medium"
              >
                admin@shotclockdating.com
              </a>
            </p>
            <p className="text-gray-700 mt-2">
              <strong>Subject:</strong> Cookie Policy Inquiry
            </p>
          </div>
          
          <div className="mt-6 text-sm text-gray-600">
            <p>This Cookie Policy is part of our Privacy Policy and Terms of Service. Changes to this policy will be communicated through our platform and via email.</p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default CookiePolicy;